import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type QueryKey,
} from '@tanstack/react-query';
import { useCallback, useMemo, useRef } from 'react';
import {
  fetchLatestChatWindow,
  fetchOlderChatWindow,
} from '../history/history';
import {
  getObservedChatMessages,
  mergeChatObservations,
  type ChatMessageObservation,
} from '../history/observations';
import type {
  ChatPage,
  ChatServerMessage,
  ChatServerWindow,
} from '../history/types';

/** Marks the latest-page request whose successful result is on screen. */
export type ChatRefreshMarker = {
  sessionId: string;
  requestId: number;
};

export type UseChatHistoryOptions<TPage> = {
  /** Identifies the mounted chat; request markers are scoped to it. */
  sessionId: string;
  queryKeys: { latest: QueryKey; history: QueryKey };
  fetchPage: (page: number, signal: AbortSignal) => Promise<TPage>;
  enabled?: boolean;
  pollInterval?: number | false;
};

type CachedWindow<
  TMessage extends ChatServerMessage,
  TPage extends ChatPage<TMessage>,
> = ChatServerWindow<TMessage, TPage> & {
  observations: ChatMessageObservation<TMessage>[];
};

type LatestWindow<
  TMessage extends ChatServerMessage,
  TPage extends ChatPage<TMessage>,
> = CachedWindow<TMessage, TPage> & { refresh: ChatRefreshMarker };

// Without a page, history starts below the latest window.
type HistoryPageParam = { page: number | undefined };

const DEFAULT_POLL_INTERVAL = 10_000;

/**
 * Server messages of one chat: a polled latest page and older history loaded
 * page by page. Initial load, background refresh and history have separate state.
 */
export const useChatHistory = <
  TMessage extends ChatServerMessage,
  TPage extends ChatPage<TMessage> = ChatPage<TMessage>,
>({
  sessionId,
  queryKeys,
  fetchPage,
  enabled = true,
  pollInterval = DEFAULT_POLL_INTERVAL,
}: UseChatHistoryOptions<TPage>) => {
  const queryClient = useQueryClient();
  const latestStartedRequestId = useRef(0);
  const serverStartedRequestId = useRef(0);

  const readKnownObservations = () => {
    const latest = queryClient.getQueryData<LatestWindow<TMessage, TPage>>(
      queryKeys.latest
    );
    const history = queryClient.getQueryData<
      InfiniteData<CachedWindow<TMessage, TPage>, HistoryPageParam>
    >(queryKeys.history);
    return mergeChatObservations(
      ...[...(history?.pages ?? []), ...(latest ? [latest] : [])].map(
        window => window.observations
      )
    );
  };

  const completeWindow = (
    window: ChatServerWindow<TMessage, TPage>,
    received: ChatMessageObservation<TMessage>[]
  ): CachedWindow<TMessage, TPage> => {
    // A scan's early pages cannot overwrite a newer poll that completed
    // while the scan was still requesting its later pages.
    const observations = mergeChatObservations(
      readKnownObservations(),
      received
    );
    return {
      ...window,
      messages: getObservedChatMessages(observations),
      observations,
    };
  };

  const createFetchPage =
    (signal: AbortSignal, received: ChatMessageObservation<TMessage>[]) =>
    async (pageNumber: number) => {
      signal.throwIfAborted();
      const requestId = ++serverStartedRequestId.current;
      const page = await fetchPage(pageNumber, signal);
      // An HTTP promise may finish after its query observer unmounts.
      signal.throwIfAborted();
      received.push(...page.data.map(message => ({ message, requestId })));
      return page;
    };

  const latest = useQuery({
    queryKey: queryKeys.latest,
    queryFn: async ({ signal }): Promise<LatestWindow<TMessage, TPage>> => {
      const requestId = ++latestStartedRequestId.current;
      const received: ChatMessageObservation<TMessage>[] = [];
      const window = await fetchLatestChatWindow(
        createFetchPage(signal, received),
        getObservedChatMessages(readKnownObservations())
      );
      return {
        ...completeWindow(window, received),
        refresh: { sessionId, requestId },
      };
    },
    enabled,
    refetchInterval: pollInterval,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    staleTime: 0,
  });

  const initialPageParam: HistoryPageParam = { page: undefined };
  const history = useInfiniteQuery({
    queryKey: queryKeys.history,
    queryFn: async ({ signal, pageParam }) => {
      const page =
        pageParam.page ??
        queryClient.getQueryData<LatestWindow<TMessage, TPage>>(
          queryKeys.latest
        )?.nextPage;
      if (page === undefined) {
        throw new Error('There are no older messages to load');
      }
      const received: ChatMessageObservation<TMessage>[] = [];
      const window = await fetchOlderChatWindow(
        createFetchPage(signal, received),
        getObservedChatMessages(readKnownObservations()),
        page
      );
      return completeWindow(window, received);
    },
    initialPageParam,
    // Every window retains its confirmed predecessors; retaining previous
    // windows as well would duplicate increasingly large history snapshots.
    maxPages: 1,
    getNextPageParam: (lastPage): HistoryPageParam | undefined =>
      lastPage.nextPage ? { page: lastPage.nextPage } : undefined,
    enabled: false,
    staleTime: Infinity,
  });

  const messages = useMemo(
    () =>
      getObservedChatMessages(
        mergeChatObservations(
          ...[
            ...(history.data?.pages ?? []),
            ...(latest.data ? [latest.data] : []),
          ].map(window => window.observations)
        )
      ),
    [history.data, latest.data]
  );
  const getLatestStartedRequestId = useCallback(
    () => latestStartedRequestId.current,
    []
  );
  const loadMore = async () => {
    if (!enabled || !latest.data || history.isFetching) {
      return;
    }
    return history.fetchNextPage();
  };

  return {
    messages,
    latestPage: latest.data?.pages[0],
    historyPages: history.data?.pages.flatMap(page => page.pages) ?? [],
    initialLoad: {
      isPending: enabled && latest.isPending,
      isFetching: latest.isFetching && latest.data === undefined,
      error: latest.data ? null : latest.error,
      retry: latest.refetch,
    },
    refresh: {
      isFetching: latest.isFetching && latest.data !== undefined,
      error: latest.data ? latest.error : null,
      retry: latest.refetch,
    },
    history: {
      isFetching: history.isFetching,
      error: history.error,
      hasMore:
        history.data?.pages.at(-1)?.hasMore ?? latest.data?.hasMore ?? false,
      loadMore,
    },
    successfulRefresh: latest.data?.refresh,
    getLatestStartedRequestId,
  };
};

export type ChatHistory<TMessage extends ChatServerMessage> = ReturnType<
  typeof useChatHistory<TMessage>
>;
